import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import AutoComplete from '../components/AutoComplete';
import { serieService, serieModuloService, sucursalService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

// Rutas del listado de cada módulo para saltar desde Series
const MODULO_LISTADO = {
  RQ: '/requerimientos/listar',
  OC: '/orden-compra/listar',
  GR: '/almacen/guias-remision',
  PE: '/almacen/parte-entrada/listar',
  PS: '/almacen/parte-salida',
};

const Series = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [mostrarInactivas, setMostrarInactivas] = useState(false);

  const emptyForm = {
    codigo: '', serie: '', descripcion: '',
    ultimo_numero: 0, maximo_numeros: 7,
    id_sucursal: '', activo: true, formato_impresion: '',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ser, mod, suc] = await Promise.all([
        serieService.getAll(null, null, mostrarInactivas),
        serieModuloService.getAll(),
        sucursalService.getAll(),
      ]);
      setRecords(ser.data);
      setModulos(mod.data.map(m => ({ ...m, descripcion: `${m.codigo} - ${m.descripcion}` })));
      setSucursales(suc.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar series.' });
    } finally {
      setLoading(false);
    }
  }, [mostrarInactivas]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const codigo = String(row['Codigo'] || row['codigo'] || '').toUpperCase();
        const serie = String(row['Serie'] || row['serie'] || '').trim().toUpperCase();
        const descripcion = String(row['Descripcion'] || row['descripcion'] || '');
        let errorMsg = null;
        if (!codigo || !serie || !descripcion) {
          fail++;
          errorMsg = `Fila ${i + 1}: Codigo, Serie y Descripcion son obligatorios`;
        } else {
          try {
            await serieService.create({
              codigo,
              serie,
              descripcion,
              ultimo_numero: parseInt(row['Ultimo Numero'] || row['ultimo_numero'] || 0) || 0,
              maximo_numeros: parseInt(row['Maximo Numeros'] || row['maximo_numeros'] || 7) || 7,
              activo: true,
            });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${codigo}-${serie}"): ${getErrorDetail(err, 'Error al crear')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      codigo: r.codigo || '',
      serie: r.serie || '',
      descripcion: r.descripcion || '',
      ultimo_numero: r.ultimo_numero ?? 0,
      maximo_numeros: r.maximo_numeros ?? 7,
      id_sucursal: r.id_sucursal || '',
      activo: r.activo === true || r.activo === 1,
      formato_impresion: r.formato_impresion || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo || !form.serie || !form.descripcion) {
      setAlert({ type: 'warning', message: 'Codigo, Serie y Descripcion son obligatorios.' });
      return;
    }
    const serie_pad = String(form.serie).trim().toUpperCase();
    if (!/^[A-Z0-9]{4}$/.test(serie_pad)) {
      setAlert({ type: 'warning', message: 'La serie debe tener exactamente 4 caracteres alfanuméricos (ej: T001, GR01, 0001).' });
      return;
    }
    const maxNum = parseInt(form.maximo_numeros);
    if (isNaN(maxNum) || maxNum < 1 || maxNum > 7) {
      setAlert({ type: 'warning', message: 'Máximo de números debe estar entre 1 y 7.' });
      return;
    }
    const data = {
      codigo: form.codigo.toUpperCase(),
      serie: serie_pad, // ya es uppercase y exactamente 4 chars
      descripcion: form.descripcion,
      ultimo_numero: parseInt(form.ultimo_numero) || 0,
      maximo_numeros: maxNum,
      id_sucursal: form.id_sucursal === '' ? null : parseInt(form.id_sucursal),
      activo: form.activo,
      formato_impresion: form.formato_impresion || null,
    };
    try {
      if (editing) await serieService.update(editing.id_serie, data);
      else await serieService.create(data);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Serie',
      message: `¿Eliminar la serie "${r.codigo}-${r.serie}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try { await serieService.delete(r.id_serie); setToast('Serie eliminada'); fetchData(); }
        catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); }
      },
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const getModuloLabel = (codigo) => {
    const m = modulos.find(x => x.codigo === codigo);
    return m ? m.descripcion : codigo || '--';
  };

  const getSucursalLabel = (id) => {
    if (!id) return 'Todas';
    const s = sucursales.find(x => x.id_sucursal === id);
    return s ? s.nombre : '--';
  };

  const maxEjemplo = (n) => '0'.repeat(n - 1) + '1';

  const formatUltimoComprobante = (r) => {
    const n = parseInt(r.ultimo_numero) || 0;
    if (n <= 0) return null;
    return `${r.codigo}-${r.serie}-${String(n).padStart(r.maximo_numeros || 7, '0')}`;
  };

  const irAlListadoModulo = (r) => {
    const ruta = MODULO_LISTADO[r.codigo];
    if (!ruta) {
      setAlert({ type: 'info', message: `El módulo "${r.codigo}" aún no tiene listado implementado.` });
      return;
    }
    const ultimo = formatUltimoComprobante(r);
    navigate(ruta, { state: { filtroSerie: r.serie, filtroNumero: r.ultimo_numero, ultimoComprobante: ultimo } });
  };

  const columns = [
    { key: 'codigo', label: 'Modulo', width: 90, style: { fontWeight: 600 }, render: (r) => r.codigo },
    { key: 'serie', label: 'Serie', width: 80, style: { fontWeight: 600 } },
    { key: 'descripcion', label: 'Descripcion' },
    { key: 'correlativo', label: 'Formato', width: 160,
      render: (r) => `${r.serie}-${maxEjemplo(r.maximo_numeros)}` },
    {
      key: 'ultimo_comprobante', label: 'Último Comprobante', width: 220,
      render: (r) => {
        const ultimo = formatUltimoComprobante(r);
        if (!ultimo) return <span style={{ color: '#64748b', fontStyle: 'italic' }}>Sin usar</span>;
        const clicable = !!MODULO_LISTADO[r.codigo];
        return (
          <button
            type="button"
            onClick={() => clicable && irAlListadoModulo(r)}
            disabled={!clicable}
            title={clicable ? 'Ir al listado del módulo para ver/editar' : 'Módulo sin listado disponible'}
            style={{
              background: clicable ? 'rgba(96,165,250,0.1)' : 'transparent',
              border: `1px solid ${clicable ? 'rgba(96,165,250,0.3)' : 'rgba(148,163,184,0.2)'}`,
              color: clicable ? '#60a5fa' : '#94a3b8',
              padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
              fontFamily: 'monospace', cursor: clicable ? 'pointer' : 'default',
            }}
          >
            {ultimo}
          </button>
        );
      },
    },
    { key: 'id_sucursal', label: 'Sucursal', width: 130, render: (r) => getSucursalLabel(r.id_sucursal) },
    { key: 'activo', label: 'Estado', width: 90,
      render: (r) => (
        <span style={{ color: (r.activo === true || r.activo === 1) ? '#4caf7d' : '#e05c5c', fontWeight: 600 }}>
          {(r.activo === true || r.activo === 1) ? 'Activa' : 'Inactiva'}
        </span>
      )
    },
  ];

  const importCols = [
    { key: 'Codigo', label: 'Codigo', required: true, example: 'RQ', description: 'Codigo del módulo (RQ, OC, GS...)' },
    { key: 'Serie', label: 'Serie', required: true, example: '0001', description: 'Serie de 4 dígitos (ej: 0001)' },
    { key: 'Descripcion', label: 'Descripcion', required: true, example: 'Requerimientos Lima', description: 'Descripcion de la serie' },
    { key: 'Maximo Numeros', label: 'Maximo Numeros', required: false, example: '7', description: 'Dígitos del correlativo (1-7, default 7)' },
    { key: 'Ultimo Numero', label: 'Ultimo Numero', required: false, example: '0', description: 'Último correlativo usado (default 0)' },
  ];

  return (
    <Layout title="Series" subtitle="Utilitarios">
      <div className="page-header">
        <h2>Series</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            padding: '6px 12px', borderRadius: 8,
            background: mostrarInactivas ? 'rgba(251,191,36,0.1)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${mostrarInactivas ? 'rgba(251,191,36,0.35)' : 'rgba(255,255,255,0.08)'}`,
            color: mostrarInactivas ? '#fbbf24' : '#94a3b8',
            fontSize: 12.5, fontWeight: 600, transition: 'all 0.15s ease',
            userSelect: 'none',
          }}>
            <input type="checkbox" checked={mostrarInactivas}
              onChange={e => setMostrarInactivas(e.target.checked)}
              style={{ width: 'auto', margin: 0, accentColor: '#fbbf24' }} />
            Mostrar inactivas
          </label>
          <button className="btn btn-primary" onClick={openCreate}>+ Nueva</button>
        </div>
      </div>

      <DataTable columns={columns} data={records} idField="id_serie"
        onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay series registradas"
        exportFileName="series" importColumns={importCols} />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2>{editing ? 'Editar Serie' : 'Nueva Serie'}</h2>
                {!editing && <span className="modal-header-badge"><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg></span>}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">

                {/* Modulo autocomplete */}
                <AutoComplete
                  label="Módulo *"
                  value={form.codigo}
                  options={modulos}
                  idField="codigo"
                  displayField="descripcion"
                  onChange={(val) => setForm(f => ({ ...f, codigo: val }))}
                  placeholder="Buscar módulo (RQ, OC, GS...)"
                />

                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Serie *</label>
                    <input name="serie" value={form.serie} onChange={handleChange}
                      placeholder="GS01, 0001..." maxLength={4}
                      style={{ textTransform: 'uppercase' }}
                      onBlur={() => setForm(f => ({ ...f, serie: f.serie.trim().toUpperCase() }))} />
                  </div>
                  <div className="form-row">
                    <label>Descripcion *</label>
                    <input name="descripcion" value={form.descripcion} onChange={handleChange}
                      placeholder="Ej: Requerimientos Lima" />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Último Número</label>
                    <input name="ultimo_numero" type="number" min="0" value={form.ultimo_numero}
                      onChange={handleChange} />
                  </div>
                  <div className="form-row">
                    <label>Máx. Dígitos</label>
                    <input name="maximo_numeros" type="number" min="1" max="7" value={form.maximo_numeros}
                      onChange={handleChange} />
                  </div>
                  <div className="form-row" style={{ paddingTop: 4 }}>
                    <label>Formato</label>
                    <div style={{ padding: '6px 0', color: '#8899aa', fontSize: 13, fontFamily: 'monospace' }}>
                      {String(form.serie || '????').toUpperCase()}-{maxEjemplo(parseInt(form.maximo_numeros) || 7)}
                    </div>
                  </div>
                </div>

                {/* Sucursal autocomplete */}
                <AutoComplete
                  label="Sucursal"
                  value={form.id_sucursal === '' ? '' : String(form.id_sucursal)}
                  options={sucursales}
                  idField="id_sucursal"
                  displayField="nombre"
                  onChange={(val) => setForm(f => ({ ...f, id_sucursal: val }))}
                  placeholder="Todas las sucursales"
                />

                <div className="form-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingTop: 4 }}>
                  <input type="checkbox" name="activo" id="chk_activo"
                    checked={form.activo} onChange={handleChange}
                    style={{ width: 'auto', margin: 0 }} />
                  <label htmlFor="chk_activo" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 13 }}>
                    Serie activa
                  </label>
                </div>

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default Series;
